<script>
import { preventDefault, stopPropagation } from "svelte/legacy";
import { onMount, tick } from "svelte";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { lockState } from "@app/store/lockStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { editorState, filterState } from "@app/store/editorStore.svelte.js";
import { showModal } from "@app/store/modalStore.svelte.js";
import * as m from '@app/paraglide/messages.js';
import { Circle } from "svelte-loading-spinners";
import { decode } from "html-entities";
import QuillEditor from "./QuillEditor.svelte";
import TranscriptEditorPreview from "./TranscriptEditorPreview.svelte";
import TcInput from "./inputs/TcInput.svelte";
import SliderInput from "./inputs/SliderInput.svelte";
import RealTimeErrors from "./RealTimeErrors.svelte";
import EventNotes from "./EventNotes.svelte";
import EventApproval from "./EventApproval.svelte";
import ForcedSubtitleEvent from "./ForcedSubtitleEvent.svelte";
import EventMetrics from "./EventMetrics.svelte";
import EventSpeakers from "./EventSpeakers.svelte";
import EventRecipients from "./EventRecipients.svelte";
import EventTags from "./EventTags.svelte";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import sccLib from "@cclib/dict/608.js";
import tcLib from "@cclib/lib/timecode.js";
import insertEv from "@cclib/functions/eventGroups/insertEvent.js";
import removeEvent from "@cclib/functions/eventGroups/removeEvent.js";
import { clickoutside } from "@svelte-put/clickoutside";

// Import the shared utility functions from styleGuideTests.js
import {
    getTotalChars,    
    calculateCps,
    calculateWpm,
    calculateDuration,
} from "@cclib/lib/styleGuideTests.js";
import { getFilteredEventIndices } from "@app/lib/eventValidation.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";
import { assignEventAudio } from "@app/lib/audio/assignEventAudio.js";
import {
    WEB_SPEECH_PROVIDER,
    cancel as cancelWebSpeech,
    speak as speakWebSpeech,
} from "@app/lib/audio/webSpeechTts.js";
import {
    renderParentAudio,
    getAudioDurationFromUrl,
    MAX_TTS_CHARS,
} from "@app/lib/audio/parentTts.js";

/* Firebase */
import firebase from "@app/configs/firebase.js";
import "firebase/compat/functions";

/** @type {{eventIndex: any}} */
let { eventIndex } = $props();
let event = $derived(eventGroupState[projectState.selected]?.events[eventIndex]);
let rendering = $state(false);
let showAiTools = $state(false);
let aiToolsBtnEl = $state(null);
let aiMenuPos = $state({ top: 0, left: 0 });
let previewAudioPlayer;
let previewAudioPlaying = $state(false);
let webSpeechPreviewStopRequested = false;

const audioPreviewStartedEvent = "audioDescriptionPreviewAudioStarted";

let plainText = $derived(convertToPlainText(event?.text  || ""));
let isWebSpeechVoice = $derived(event?.voice?.provider === WEB_SPEECH_PROVIDER);
let canPreviewAudio = $derived(isWebSpeechVoice ? plainText.trim().length > 0 : !!event?.audioFile);

let lineInfo = $derived.by(() => {
    let lines = [];
    plainText.split("\n").forEach((line) => {
        lines.push(decode(line).length);
    });

    return lines;
});

let totalWords = $derived(plainText.split(/\s/gm).length);

let totalChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Use the imported getTotalChars function
    return getTotalChars(plainText, "text");
});

let cpsChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Reading rate (CPS) can optionally exclude spaces based on user metrics settings
    return getTotalChars(plainText, "text", metricsState.cpsIncludeSpaces === false);
});

let duration = $derived(calculateDuration(event.start, event.end, 3));
let wpm = $derived(calculateWpm(totalWords, duration));
let cps = $derived(calculateCps(cpsChars, duration));

let overlaps = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return null;
    if (event.start === false || event.end === false) return null;
    if (event.start < eventGroupState[projectState.selected]?.events[eventIndex-1]?.end || event.end > eventGroupState[projectState.selected]?.events[eventIndex+1]?.start) {
        return true;
    }

    return false;    
});

let illegalChars = $derived.by(() => {
    return plainText.split("").find((char) => {
        return sccLib.valid.indexOf(char) === -1;
    });
});

let errorMsg = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return null;
    let messages = [];

    if (overlaps && eventGroupState[projectState.selected].overlap) {
        messages.push("• Event overlap detected.");
    }

    if (eventGroupState[projectState.selected].illegalChars && illegalChars) {
        messages.push(
            `• Illegal non-608 compliant character found ${illegalChars}`,
        );
    }

    if (
        lineInfo.some((line) => {
            return line > eventGroupState[projectState.selected]?.maxChars;
        })
    ) {
        messages.push(
            `• Event exceeds maximum character count of ${eventGroupState[projectState.selected]?.maxChars}.`,
        );
    }

    if (lineInfo.length > eventGroupState[projectState.selected]?.maxLines) {
        messages.push(
            `• Event exceeds maximum line count of ${eventGroupState[projectState.selected]?.maxLines}.`,
        );
    }

    if (event.start !== false && event.end !== false){
        if (duration < eventGroupState[projectState.selected]?.minDuration){
            messages.push(
                `• Event is less than the minimum duration of ${eventGroupState[projectState.selected]?.minDuration} seconds.`,
            );
        } else if (duration > eventGroupState[projectState.selected]?.maxDuration){
            messages.push(
                `• Event exceeds maximum duration of ${eventGroupState[projectState.selected]?.maxDuration} seconds.`,
            );
        } else if (cps > eventGroupState[projectState.selected]?.maxCps){
            messages.push(
                `• Event exceeds maximum CPS (${eventGroupState[projectState.selected]?.maxCps}).`,
            );
        } else if (wpm > eventGroupState[projectState.selected]?.maxWpm){
            messages.push(
                `• Event exceeds maximum WPM (${eventGroupState[projectState.selected]?.maxWpm}).`,
            );
        }
    }

    return messages.join("\n");
});

onMount(() => {
    function stopOtherPreviewAudio(previewEvent) {
        if (previewEvent.detail?.eventIndex !== eventIndex) {
            stopPreviewAudio();
        }
    }

    window.addEventListener(audioPreviewStartedEvent, stopOtherPreviewAudio);

    return () => {
        stopPreviewAudio();
        window.removeEventListener(audioPreviewStartedEvent, stopOtherPreviewAudio);
    };
});

function selectEvent(clickEvent, index) {
    try {
        if (!eventGroupState[projectState.selected]) return;

        if (clickEvent.ctrlKey || clickEvent.metaKey) {
            // Toggle selection with Ctrl/Cmd+Click
            if (
                eventGroupState[projectState.selected].selected.indexOf(index) >
                -1
            ) {
                eventGroupState[projectState.selected].selected =
                    eventGroupState[projectState.selected].selected.filter(
                        (event) => {
                            return event !== index;
                        },
                    );
            } else {
                eventGroupState[projectState.selected].selected = [
                    ...eventGroupState[projectState.selected].selected,
                    index
                ];
            }
        } else if (
            clickEvent.shiftKey &&
            eventGroupState[projectState.selected].selected.length > 0
        ) {
            // Range selection with Shift+Click - only select visible/filtered events
            const startIndex = eventGroupState[projectState.selected].selected[0];
            const endIndex = index;
            const minIndex = Math.min(startIndex, endIndex);
            const maxIndex = Math.max(startIndex, endIndex);
            
            // Get all filtered/visible event indices
            const events = eventGroupState[projectState.selected].events;
            const eventGroup = eventGroupState[projectState.selected];
            const filteredIndices = getFilteredEventIndices(events, eventGroup, filterState);
            
            // Filter to only include indices within the selection range
            const selectedIndices = filteredIndices.filter(i => i >= minIndex && i <= maxIndex);
            eventGroupState[projectState.selected].selected = selectedIndices;
        } else {
            // Normal selection
            eventGroupState[projectState.selected].selected = [index];

            if (
                lockState.video && eventGroupState[projectState.selected].events[index].start &&
                !isNaN(
                    eventGroupState[projectState.selected].events[index].start,
                )
            ) {
                player.currentTime =
                    eventGroupState[projectState.selected].events[index].start;
            }

            if (eventGroupState[projectState.selected].selected[0] >= 0) {
                window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                    detail: { index: index }
                }))
            }
        }
    } catch (err) {
        console.log("There was an error selecting the event. " + err.message);
        console.log(err);
    }
}

function insertEvent() {
    try {
        if (!eventGroupState[projectState.selected]) {
            return;
        }

        let eventOptions;
        if (eventGroupState[projectState.selected].events.length > 0) {
            try {
                let eventTemplate =
                    eventGroupState[projectState.selected].selected.length > 0
                        ? eventGroupState[projectState.selected].events[
                              eventGroupState[projectState.selected].selected[0]
                          ]
                        : eventGroupState[projectState.selected].events[
                              eventGroupState[projectState.selected].events
                                  .length - 1
                          ];

                eventOptions = {
                    alignment: eventTemplate.alignment,
                    xPos: eventTemplate.xPos,
                    yPos: eventTemplate.yPos,
                    vertical: eventTemplate.vertical,
                    voice: eventTemplate.voice,
                    speakingStyle: eventTemplate.speakingStyle,
                    style: eventTemplate.style,
                    rate: eventTemplate.rate,
                    start: editorState.insertStartOnInsert
                        ? player.currentTime
                        : false,
                    end: editorState.insertStartOnInsert
                        ? player.currentTime +
                          eventGroupState[projectState.selected].minDuration
                        : false,
                };
            } catch (err) {
                console.log(err);
            }
        }

        // Insert new event
        eventGroupState[projectState.selected] = insertEv(
            eventGroupState[projectState.selected],
            eventOptions,
            eventIndex + 1,
        );        setTimeout(function () {
            eventGroupState[projectState.selected].selected = [eventIndex + 1];
            const selectedIndex = eventIndex + 1;

            // Dispatch custom event to scroll to the newly inserted event
            window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                detail: { index: selectedIndex }
            }));
        }, 20);

        /* Store in History */
        insertHistoryAction({
            name: "insert event", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

async function deleteEvent() {
    try {
        if (!eventGroupState[projectState.selected]) {
            return;
        }

        // Remove the event
        const eventIndexToDelete =  eventIndex;      
        // Update selection to previous event or clear if no events left
        let newSelectedIndex = eventIndexToDelete > 0 ? eventIndexToDelete - 1 : 0;
        if (!eventGroupState[projectState.selected].events[newSelectedIndex]) {
            eventGroupState[projectState.selected].selected = [];
        } else {
            eventGroupState[projectState.selected].selected = [newSelectedIndex];
            // Dispatch custom event to scroll to the selected event
            window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                detail: { index: newSelectedIndex }
            }));
        }

        await tick();

        eventGroupState[projectState.selected] = removeEvent(eventGroupState[projectState.selected], eventIndexToDelete);

        /* Store in History */
        insertHistoryAction({
            name: "delete event", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

async function trimToDuration() {
    event.end = event.start + event.audioFileDuration + 0.5;
}

async function previewAudio() {
    if (previewAudioPlaying) {
        stopPreviewAudio();
        return;
    }

    if (!canPreviewAudio || (!isWebSpeechVoice && !previewAudioPlayer)) {
        return;
    }

    try {
        window.dispatchEvent(
            new CustomEvent(audioPreviewStartedEvent, {
                detail: { eventIndex },
            }),
        );

        // Select this event so overlays/scroll track it.
        eventGroupState[projectState.selected].selected = [eventIndex];

        // The AD synth voice plays from this event's independent audio source,
        // so AdPreview must not double-play the same event's audio. Set this
        // before seeking so the player's "seeked" handler doesn't cancel it.
        playerState.previewPlaying = true;

        // Seek the video to the event start so it plays in sync with the voice.
        // Always drive the video, independent of the video-lock toggle.
        try {
            if (
                typeof player !== "undefined" &&
                player &&
                event.start !== false &&
                !isNaN(event.start)
            ) {
                player.currentTime = event.start;
            }
        } catch (err) {
            console.log(err.message);
        }

        if (isWebSpeechVoice) {
            webSpeechPreviewStopRequested = false;
            previewAudioPlaying = true;
            playVideoInSync();
            await speakWebSpeech(plainText, {
                voiceURI: event.voice.voiceURI,
                rate: parseFloat(event.rate || 1),
                pitch: parseFloat(event.pitch ?? 1),
                volume: 1,
            });
            stopPreviewAudio();
            return;
        }

        previewAudioPlayer.src = event.audioFile;
        previewAudioPlayer.currentTime = 0;
        previewAudioPlayer.load();
        await previewAudioPlayer.play();
        previewAudioPlaying = true;
        playVideoInSync();
    } catch (err) {
        previewAudioPlaying = false;
        playerState.previewPlaying = false;
        console.log(err.message);
        if (!webSpeechPreviewStopRequested) {
            toast.push(err.message, {
                classes: ["toast-danger"],
            });
        }
    }
}

function playVideoInSync() {
    try {
        if (typeof player !== "undefined" && player?.play) {
            Promise.resolve(player.play()).catch(() => {});
        }
    } catch (err) {
        console.log(err.message);
    }
}

function stopPreviewAudio() {
    const wasPlaying = previewAudioPlaying;
    webSpeechPreviewStopRequested = true;
    cancelWebSpeech();

    if (previewAudioPlayer) {
        previewAudioPlayer.pause();
        previewAudioPlayer.currentTime = 0;
        previewAudioPlayer.removeAttribute("src");
    }

    previewAudioPlaying = false;

    // Only tear down shared video/preview state when THIS instance owned the
    // active preview, so the "stop other previews" listener on idle instances
    // does not pause the video or clear the suppression flag.
    if (wasPlaying) {
        playerState.previewPlaying = false;
        try {
            if (typeof player !== "undefined" && player?.pause) {
                player.pause();
            }
        } catch (err) {
            console.log(err.message);
        }
    }
}

function handlePreviewAudioEnded() {
    stopPreviewAudio();
}

function handlePreviewAudioError() {
    previewAudioPlaying = false;
    toast.push("Unable to preview audio.", {
        classes: ["toast-danger"],
    });
}

function attachPreviewAudioPlayer(node) {
    previewAudioPlayer = node;

    return () => {
        if (previewAudioPlayer === node) {
            previewAudioPlayer = undefined;
        }
    };
}

function updateAiMenuPosition() {
    if (!aiToolsBtnEl) return;
    const rect = aiToolsBtnEl.getBoundingClientRect();
    aiMenuPos = { top: rect.bottom + 2, left: rect.left };
}

function toggleAiToolsMenu() {
    setTimeout(() => {
        showAiTools = !showAiTools;
        if (showAiTools) {
            updateAiMenuPosition();
        }
    }, 0);
}

function closeAiToolsMenu() {
    if (showAiTools) {
        showAiTools = false;
    }
}

/**
 * Svelte 5 attachment that teleports a node to document.body, and also
 * closes the AI tools menu on any scroll (virtual list scroller included)
 * so the menu doesn't float in a stale position above unrelated rows.
 */
function portalAiMenu(node) {
    document.body.appendChild(node);
    const onScroll = () => closeAiToolsMenu();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onScroll);
        node.remove();
    };
}

async function aiRephrase(rate) {
    try {
        rendering = true;
        closeAiToolsMenu();

        let text = convertToPlainText(
            event.text,
            " ",
        );
        let duration, wpm;

        if (!text) {
            return;
        }

        if (
            !isNaN(
                event.end,
            ) &&
            !isNaN(
                event.start,
            )
        ) {
            duration =
                event.end -
                event.start;
            wpm = text.split(" ").length / (duration / 60);
        }

        /* console.log("Duration of event: "+ duration);
        console.log("WPM: "+ wpm);
        console.log("Event Text: "+ text); */

        let res = await firebase
            .functions()
            .httpsCallable("v8AdScriptAssistant")({
            text: text,
            rate: rate,
            wpm: wpm,
            numberOfWords: text.split(" ").length,
            duration: duration,
            groupId: eventGroupState[projectState.selected].id,
            eventId:
                event.id,
        });

        // console.log(res.data);

        event.text =
            convertToHtml(res.data.text);

        try {
            player.pause();
        } catch (err) {
            console.log(err.message);
        }

        eventGroupState[projectState.selected].selected = [];

        setTimeout(() => {
            console.log("Setting rendered to false in AI Rephrase");
            event.rendered = false;
        }, 50);

        /* Store in History */
        insertHistoryAction({
            name: "rephrase text", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.audio_description_rephrase_failed_toast({ message: err.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        rendering = false;
    }
}

function renderAudio() {
    eventGroupState[projectState.selected].selected = [eventIndex];
    showModal("renderAudio");
}

function getRateLimits(provider) {
    if (provider === "ElevenLabs") {
        return { min: 0.7, max: 1.2 };
    } else if (provider === "deepgram") {
        return { min: 0.5, max: 1.5 };
    }
    return { min: 0.5, max: 3 };
}

function clampRate(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

async function renderSingleEvent(evt) {
    if (evt?.voice?.provider === WEB_SPEECH_PROVIDER) return;

    const plainText = convertToPlainText(evt.text, " ");
    if (!plainText) return;

    let durationSec;

    if (environment.adLicense) {
        // Route through the embedding parent host (e.g. Unity) local TTS.
        if (plainText.length > MAX_TTS_CHARS) {
            throw new Error(
                `Text exceeds the ${MAX_TTS_CHARS} character limit for local text-to-speech.`,
            );
        }

        const result = await renderParentAudio({
            text: plainText,
            voice: evt.voice.id,
            speed: parseFloat(evt.rate),
            format: "mp3",
            eventId: evt.id,
        });

        if (!result || result.success === false || !result.url) {
            throw new Error(
                result?.message || "No response from text-to-speech service.",
            );
        }

        durationSec = await getAudioDurationFromUrl(result.url);
        await assignEventAudio(evt, {
            audioFile: result.url,
            audioFileDuration: durationSec,
        });
    } else {
        const res = await firebase
            .functions()
            .httpsCallable("v8TextToSpeechProviderV4")({
                provider: evt.voice.provider,
                language: evt.voice.language,
                voice: evt.voice.name,
                voice_id: evt.voice.id,
                stability: evt.voiceStability / 100,
                similarity: evt.voiceSimilarity / 100,
                speed: parseFloat(evt.rate),
                style: evt.speakingStyle,
                audioEncoding: "MP3",
                text: plainText,
                eventGroupId: eventGroupState[projectState.selected].id,
                eventId: evt.id,
            });

        if (!res?.data) {
            throw new Error("No response from text-to-speech service.");
        }

        durationSec = tcLib.tcMsToSec(res.data.duration);
        await assignEventAudio(evt, {
            audioFile: res.data.audio,
            audioFileDuration: durationSec,
        });
    }

    if (editorState.autoTrimEvent && !isNaN(evt.start) && durationSec > 0) {
        evt.end = evt.start + durationSec + 0.5;
    }
}

async function autoRate() {
    try {
        if (!event?.voice || event.voice.name === "Manual Record") return;
        if (event.voice.provider === WEB_SPEECH_PROVIDER) return;
        if (isNaN(event.start) || isNaN(event.end) || event.end <= event.start) return;

        const plainText = convertToPlainText(event.text || "", " ").trim();
        if (!plainText) return;

        const eventDuration = event.end - event.start;
        const TOLERANCE = 0.5; // seconds
        const MAX_ATTEMPTS = 2; // total renders after baseline

        const { min, max } = getRateLimits(event.voice.provider);

        rendering = true;

        if (!event.audioFileDuration || !event.rendered) {
            await renderSingleEvent(event);
        }

        for (let i = 0; i < MAX_ATTEMPTS; i++) {
            if (!event.audioFileDuration) break;

            const diff = event.audioFileDuration - eventDuration;
            if (Math.abs(diff) <= TOLERANCE) break;

            const desiredRate = (event.audioFileDuration / eventDuration) * event.rate;
            const newRate = parseFloat(clampRate(desiredRate, min, max).toFixed(2));

            if (Math.abs(newRate - event.rate) < 0.005) break;

            event.rate = newRate;
            await renderSingleEvent(event);
        }

        insertHistoryAction({
            name: "auto rate",
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.event_ad_auto_rate_failed_toast({ message: err.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        rendering = false;
    }
}

function recordAudio() {
    eventGroupState[projectState.selected].selected = [eventIndex];
    showModal("audioRecorder");
}
</script>

<audio
    {@attach attachPreviewAudioPlayer}
    class="d-none"
    preload="none"
    onended={handlePreviewAudioEnded}
    onerror={handlePreviewAudioError}>
</audio>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<form
    id="Event{eventIndex}"
    aria-label={m.event_audio_description_aria_label({eventIndex})}
    onclick={(clickEvent) => selectEvent(clickEvent, eventIndex)}
    class="rounded-0 p-3 eventItem {eventGroupState[projectState.selected] &&
    eventGroupState[projectState.selected].selected.indexOf(eventIndex) > -1
        ? 'border border-warning bg-warning bg-opacity-25'
        : ''}">
    <div class="row">
        <!-- Event Metrics -->
        <div class="col-2">
            <div class="btn-group float-end" role="group">
                <RealTimeErrors {errorMsg}></RealTimeErrors>
                {#if event}
                    <ForcedSubtitleEvent bind:event></ForcedSubtitleEvent>
                    <EventApproval bind:event></EventApproval>
                    <EventNotes bind:event></EventNotes>
                {/if}
            </div>

            <p class="text-dark mb-1 fw-bold" title={m.event_id_title()}>
                {eventIndex + 1}
            </p>
            <p
                class="text-truncate text-custom-small text-dark fst-italic m-0"
                title={event
                    .voice.name}>
                {event.voice
                    .name}
            </p>
            <select
                class="text-custom-medium speakerStyleSelect mb-2 w-100"
                bind:value={event.speakingStyle}
                onchange={() => {
                    event.rendered = false;
                }}>
                +
                {#each event.voice.styles as styleOption (styleOption)}
                    <option>{styleOption}</option>
                {/each}
            </select>

            <EventMetrics
                {wpm}
                {cps}
                {duration}
                {totalWords}
                {lineInfo}
                {totalChars}
                eventGroupSettings={{
                    maxChars: eventGroupState[projectState.selected].maxChars,
                    maxLines: eventGroupState[projectState.selected].maxLines,
                    maxCps: eventGroupState[projectState.selected].maxCps,
                    maxWpm: eventGroupState[projectState.selected].maxWpm,
                    minDuration:
                        eventGroupState[projectState.selected].minDuration,
                    maxDuration:
                        eventGroupState[projectState.selected].maxDuration,
                }}></EventMetrics>

            {#if rendering}
                <Circle size="30" color="#1eb4b2" unit="px" duration="1s"
                ></Circle>
            {/if}
        </div>
        <!-- Event Timecode -->
        <div class="col-auto">
            <div class="input-group input-group-sm mb-1">
                <TcInput bind:event tcType="start"></TcInput>
            </div>
            <div class="input-group input-group-sm mb-2">
                <TcInput bind:event tcType="end"></TcInput>
            </div>
            {#if event?.voice?.provider === WEB_SPEECH_PROVIDER}
                <SliderInput
                    bind:event
                    property="rate"
                    label="Rate"
                    minValue="0.5"
                    maxValue="3"
                    stepValue="0.01"
                    unit="x"></SliderInput>
                <SliderInput
                    bind:event
                    property="pitch"
                    label={m.event_ad_pitch_label()}
                    minValue="0"
                    maxValue="2"
                    stepValue="0.01"
                    unit="x"></SliderInput>
            {:else if event?.voice?.provider === "ElevenLabs"}
                <SliderInput
                    bind:event
                    property="rate"
                    label="Rate"
                    minValue="0.7"
                    maxValue="1.2"
                    stepValue="0.01"
                    unit="x"></SliderInput>
                <SliderInput
                    bind:event
                    property="voiceStability"
                    label="Stability"
                    minValue="30"
                    maxValue="100"
                    stepValue="1"
                    unit="%"></SliderInput>
                <SliderInput
                    bind:event
                    property="voiceSimilarity"
                    label="Similarity"
                    minValue="30"
                    maxValue="100"
                    stepValue="1"
                    unit="%"></SliderInput>
            {:else if event?.voice?.provider === "deepgram"}
                <SliderInput
                    bind:event
                    property="rate"
                    label="Rate"
                    minValue="0.5"
                    maxValue="2"
                    stepValue="0.01"
                    unit="x"></SliderInput>
            {:else}
                <SliderInput
                    bind:event
                    property="rate"
                    label="Rate"
                    minValue="0.5"
                    maxValue="3"
                    stepValue="0.01"
                    unit="x"></SliderInput>
            {/if}
            <div
                class="btn-group btn-group-sm w-100 shadow-sm"
                role="group">
                <!-- Record Button -->
                {#if event?.voice?.name === "Manual Record"}
                    <button
                        id="recordBtn-{eventIndex}"
                        type="button"
                        class="btn btn-light {event.audioFile ? 'text-dark' : 'text-warning'}"
                        disabled={rendering}
                        onclick={() => recordAudio()}
                        title={m.event_ad_record_voiceover_title()}
                        aria-label={m.event_ad_record_voiceover_aria_label()}>
                        <i class="bi bi-mic-fill"></i>
                    </button>
                {:else if (environment.online || environment.adLicense) && !isWebSpeechVoice}
                    <button
                        id="renderBtn-{eventIndex}"
                        type="button"
                        class="btn {!event.rendered
                            ? 'btn-warning text-white glowing-button'
                            : 'btn-light text-dark'}"
                        disabled={rendering}
                        onclick={() => renderAudio()}
                        title={m.event_ad_render_tts_title()}
                        aria-label={m.event_ad_render_tts_aria_label()}>
                        <i class="bi bi-bootstrap-reboot"></i>
                    </button>
                    <button
                        id="autoRateBtn-{eventIndex}"
                        type="button"
                        class="btn btn-light text-dark"
                        disabled={rendering ||
                            isNaN(event.start) ||
                            isNaN(event.end) ||
                            event.end <= event.start ||
                            !plainText.trim()}
                        onclick={stopPropagation(preventDefault(autoRate))}
                        title={m.event_ad_auto_rate_title()}
                        aria-label={m.event_ad_auto_rate_aria_label()}>
                        <i class="bi bi-speedometer2"></i>
                    </button>
                {/if}
                <!-- Preview Button -->
                <button
                    id="previewBtn-{eventIndex}"
                    type="button"
                    class="btn btn-light text-dark"
                    disabled={(rendering && !previewAudioPlaying) ||
                        !canPreviewAudio}
                    onclick={stopPropagation(preventDefault(previewAudio))}
                    title={m.event_ad_preview_audio_title()}
                    aria-label={m.event_ad_preview_audio_aria_label()}>
                    {#if previewAudioPlaying}
                        <i class="bi bi-stop-fill"></i>
                    {:else}
                        <i class="bi bi-play-fill"></i>
                    {/if}
                </button>
                <!-- Trim Button -->
                {#if environment.online || environment.adLicense}
                    <button
                        id="trimBtn-{eventIndex}"
                        type="button"
                        title={m.event_ad_trim_duration_title()}
                        aria-label={m.event_ad_trim_duration_aria_label()}
                        class="btn btn-light text-dark"
                        disabled={rendering ||
                            isNaN(
                                event.start,
                            ) ||
                            !event.audioFile}
                        onclick={trimToDuration}>
                        <i class="bi bi-scissors"></i>
                    </button>
                {/if}
                <!-- Open AI -->
                {#if environment.online}
                    <div
                        title={m.event_ad_ai_tools_title()}
                        class="btn-group btn-group-sm"
                        role="group">
                        <button
                            bind:this={aiToolsBtnEl}
                            id="aiToolsBtn-{eventIndex}"
                            type="button"
                            class="btn btn-light dropdown-toggle {showAiTools
                                ? 'show'
                                : ''}"
                            onclick={() => toggleAiToolsMenu()}
                            aria-haspopup="true"
                            aria-expanded={showAiTools}
                            aria-label={m.event_ad_ai_tools_menu_aria_label()}>
                            <i class="bi bi-cpu"></i>
                        </button>
                        {#if showAiTools}
                            <ul
                                {@attach portalAiMenu}
                                class="dropdown-menu show ai-tools-portal-menu"
                                style="position: fixed; top: {aiMenuPos.top}px; left: {aiMenuPos.left}px; margin: 0; z-index: 2000;"
                                use:clickoutside
                                onclickoutside={closeAiToolsMenu}>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase("shorten")}
                                        >{m.event_ad_rephrase_shorten()}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase("expand")}
                                        >{m.event_ad_rephrase_expand()}</a>
                                </li>
                                <li><hr class="dropdown-divider" /></li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(150)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 150 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(175)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 175 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(200)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 200 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(225)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 225 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(250)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 250 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(275)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 275 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(300)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 300 })}</a>
                                </li>
                            </ul>
                        {/if}
                    </div>
                {/if}
            </div>
            {#if event.audioFileDuration && duration < event.audioFileDuration - 0.5}
                <span
                    class="badge bg-danger p-1 mt-2"
                    title={m.event_ad_duration_warning_title()}
                    ><i class="bi bi-exclamation-lg"></i> {m.event_ad_duration_badge()}</span>
            {/if}
        </div>
        <!-- Event Text -->
        <div class="col">
            {#if eventGroupState[projectState.selected]?.selected[0] === eventIndex}
                <QuillEditor bind:event bind:eventIndex></QuillEditor>
            {:else}
                <TranscriptEditorPreview bind:event bind:eventIndex></TranscriptEditorPreview>
            {/if}
            <div class="row">
                <EventSpeakers bind:event></EventSpeakers>
                <EventRecipients bind:event></EventRecipients>
                <EventTags bind:event></EventTags>
            </div>
        </div>
    </div>
    {#if eventGroupState[projectState.selected]?.selected[0] === eventIndex}
        <div class="event-action-buttons">
            <button
                aria-label={m.event_insert_button_aria_label()}
                type="button"
                class="btn btn-primary btn-sm newEventButton"
                title={m.event_insert_button_title()}
                onclick={insertEvent}><i class="bi bi-plus-lg"></i></button>
            <button
                aria-label={m.event_delete_button_aria_label()}
                type="button"
                class="btn btn-danger btn-sm deleteEventButton"
                title={m.event_delete_button_title()}
                onclick={() => deleteEvent()}>
                <i class="bi bi-trash"></i>
            </button>
        </div>
    {/if}
</form>

<style lang="css">
.event-action-buttons {
    position: absolute;
    bottom: -10px;
    left: 5%;
    transform: translateX(-0%);
    z-index: 1059;
    display: flex;
    gap: 5px;
}

.newEventButton, .deleteEventButton {
    box-shadow: 0.3rem 0.3rem 0.3rem rgb(0 0 0 / 50%);
    color: white;
}

.eventItem {
    min-height: 229px !important;
    position: relative;
}

.speakerStyleSelect {
    padding: 0;
    background: none;
    border: none;
    border-bottom: 1px solid;
}

select > option {
    color: var(--bs-light);
}

.glowing-button {
    animation: glowing 2000ms infinite;
    box-shadow: 0 0 3px #0fbc8c;
}

@keyframes glowing {
    0% {
        background-color: #0fbc8c;
        box-shadow: 0 0 3px #0fbc8c;
    }
    50% {
        background-color: #11dda3;
        box-shadow: 0 0 10px #11dda3;
    }
    100% {
        background-color: #0de9aa;
        box-shadow: 0 0 3px #0de9aa;
    }
}
</style>
